// src/RoomForm.js

import React, { useState, useEffect, useRef } from 'react';
import { fileToDataURL, cropAndResizeDataURL } from './utils/imageUtils';
import { loadRoomAreas } from './loadRoomAreas';
import LabResults from './LabResults';
import IAQSection from './IAQSection';

export default function RoomForm({ roomData, onDataChange, onDeleteRoom }) {
  const [areaData, setAreaData] = useState(null);
  const obsUploadRef = useRef(null);

  useEffect(() => {
    loadRoomAreas()
      .then(setAreaData)
      .catch(() => setAreaData({ rooms: [], areas: {} }));
  }, []);
  if (!areaData) return null;

  const { rooms: roomList, areas: areaMap } = areaData;
  const { bathroom = [], kitchen = [], laundry = [], generic = [] } = areaMap;

  const {
    id,
    room = '',
    area = '',
    floor = '',
    compassDirection = '',
    observations = '',
    observationsPhotos = [],
    labResults = [],
    iaqReadings = null,
    recommendations = '',
    requirements = { requirement: '', approxFt: '' },
  } = roomData;

  const change = (field, val) => onDataChange(id, { [field]: val });

  // Build areaList based on room name
  let areaList = generic;
  const key = room.toLowerCase();
  if (key.includes('bath'))      areaList = [...bathroom, ...generic];
  else if (key.includes('kitchen')) areaList = [...kitchen,  ...generic];
  else if (key.includes('laundry')) areaList = [...laundry,  ...generic];
  areaList = Array.from(new Set(areaList));

  // Toggle IAQ section
  const toggleIAQ = () => {
    if (iaqReadings) {
      change('iaqReadings', null);
    } else {
      change('iaqReadings', {
        temperature: '',
        relativeHumidity: '',
        aqi: '',
        temperatureHumidityPicture: null,
        pm25: '',
        pm10: '',
        particleCount: '',
        pmPicture: null,
        formaldehyde: '',
        tvoc: '',
        formaldehydeTvocPicture: null,
        co2: '',
        co2AqiPicture: null,
      });
    }
  };

  // Handle multiple observation photos
  const handleMultiple = async (field, files) => {
    const thumbs = await Promise.all(
      Array.from(files).map(async (f) => {
        const raw = await fileToDataURL(f);
        return cropAndResizeDataURL(raw, 280);
      })
    );
    change(field, [...(roomData[field] || []), ...thumbs]);
  };

  // Header text
  const headerParts = [];
  if (floor)            headerParts.push(floor);
  if (compassDirection) headerParts.push(compassDirection);
  if (room)             headerParts.push(room);
  const title = headerParts.join(' ') + (area ? ` – ${area}` : '');

  return (
    <div className="border p-4 mb-4 rounded-lg">
      <button
        className="bg-red-500 text-white px-3 py-1 rounded mb-4"
        onClick={() => onDeleteRoom(id)}
      >
        Delete Room
      </button>

      <h1 className="text-2xl font-bold mb-4">{title || `Room ${id}`}</h1>

      {/* Room */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">Room</label>
        <select
          className="w-full border rounded px-2 py-1"
          value={room}
          onChange={e => change('room', e.target.value)}
        >
          <option value="">Select Room…</option>
          {roomList.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {/* Custom Room Name */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">Custom Room Name</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          placeholder="Or type custom room"
          value={room}
          onChange={e => change('room', e.target.value)}
        />
      </div>

      {/* Area */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">Area</label>
        <select
          className="w-full border rounded px-2 py-1"
          value={area}
          onChange={e => change('area', e.target.value)}
        >
          <option value="">Select Area…</option>
          {areaList.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {/* Custom Area Name */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">Custom Area Name</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          placeholder="Or type custom area"
          value={area}
          onChange={e => change('area', e.target.value)}
        />
      </div>

      {/* Floor */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">Floor</label>
        <select
          className="w-full border rounded px-2 py-1"
          value={floor}
          onChange={e => change('floor', e.target.value)}
        >
          <option value="">Select Floor…</option>
          <option>1st Floor</option>
          <option>2nd Floor</option>
          <option>Basement</option>
          <option>Attic</option>
          <option>Other</option>
        </select>
      </div>

      {/* Compass Direction */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">Compass Direction</label>
        <select
          className="w-full border rounded px-2 py-1"
          value={compassDirection}
          onChange={e => change('compassDirection', e.target.value)}
        >
          <option value="">Select Direction…</option>
          <option>North</option>
          <option>South</option>
          <option>East</option>
          <option>West</option>
          <option>Northeast</option>
          <option>Northwest</option>
          <option>Southeast</option>
          <option>Southwest</option>
          <option>Other</option>
        </select>
      </div>

      {/* Observations */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">Observations</label>
        <textarea
          rows={4}
          className="w-full border rounded p-2"
          value={observations}
          onChange={e => change('observations', e.target.value)}
        />
      </div>

      {/* Observation Photos */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">Observation Photos</label>
        <input
          ref={obsUploadRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={e =>
            e.target.files.length &&
            handleMultiple('observationsPhotos', e.target.files)
          }
        />
        <button
          className="bg-green-600 text-white px-4 py-2 rounded"
          onClick={() => obsUploadRef.current.click()}
        >
          Upload Photos
        </button>
        <div className="flex flex-wrap gap-4 mt-2">
          {observationsPhotos.map((url, i) => (
            <div key={i}>
              <img src={url} alt="" className="w-24 h-24 object-cover rounded" />
              <button
                className="bg-red-500 text-white px-2 py-1 rounded mt-1"
                onClick={() =>
                  change(
                    'observationsPhotos',
                    observationsPhotos.filter((_, j) => j !== i)
                  )
                }
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Lab Results */}
      <LabResults
        labResults={labResults}
        addLab={() =>
          change('labResults', [
            ...labResults,
            {
              // default Lab Area to "Room – Area"
              roomArea: `${room}${area ? ' – ' + area : ''}`,
              sampleSerial: '',
              sampleMethod: '',
              airSampleType: '',
              moldName: [],
            },
          ])
        }
        updLab={(idx, field, val) => {
          const arr = [...labResults];
          arr[idx][field] = val;
          change('labResults', arr);
        }}
        delLab={idx => change('labResults', labResults.filter((_, j) => j !== idx))}
        moldChange={(idx, opts) => {
          const arr = [...labResults];
          arr[idx].moldName = opts.map(o => o.value);
          change('labResults', arr);
        }}
      />

      {/* Recommendations */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">Recommendations</label>
        <textarea
          rows={3}
          className="w-full border rounded p-2"
          value={recommendations}
          onChange={e => change('recommendations', e.target.value)}
        />
      </div>

      {/* Requirements */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">Remediation Requirements</label>
        <textarea
          rows={2}
          className="w-full border rounded p-2"
          value={requirements.requirement}
          onChange={e =>
            change('requirements', { ...requirements, requirement: e.target.value })
          }
        />
        <div className="mt-2">
          <label className="block mb-1 font-medium">Approx. FT²</label>
          <input
            type="text"
            className="w-full border rounded px-2 py-1"
            value={requirements.approxFt}
            onChange={e =>
              change('requirements', { ...requirements, approxFt: e.target.value })
            }
          />
        </div>
      </div>

      {/* IAQ Toggle & Section */}
      <div className="mb-4">
        <button
          className="bg-gray-200 text-gray-800 px-3 py-1 rounded"
          onClick={toggleIAQ}
        >
          {iaqReadings ? 'Remove Indoor Air Quality' : 'Add Indoor Air Quality'}
        </button>
      </div>
      {iaqReadings && (
        <IAQSection
          iaqReadings={iaqReadings}
          updIAQ={newIaq => change('iaqReadings', newIaq)}
        />
      )}
    </div>
  );
}
